import { getDocs } from 'firebase/firestore';
import type { PlatformCapabilities } from '../../platform/platformCapabilities';
import { getCurrentPlatformCapabilities } from '../../platform/platformCapabilities';
import type { NormalizedLibraryProjectSource, PracticeProjectSource } from '../types';
import { userProjectsCollRef, userVideosCollRef } from '../utils';

export const FREE_UPLOAD_PROJECT_LIMIT = 30;
// Mirrored by VIDEO_FILE_LIMITS.MAX_SIZE_MB in constants.ts and by the cap in
// storage.rules; storageRules.consistency.test.ts fails if they drift apart.
export const FREE_UPLOAD_MAX_SIZE_MB = 500;

// Slot-count cap toggle. The free tier allows FREE_UPLOAD_PROJECT_LIMIT
// upload-backed projects (generous, but bounded to protect Storage cost). Set
// to `false` to make uploads unlimited by count. The size guard is independent
// of this flag and always applies.
//
// Note: checkUploadQuota() always *reports* slot_limit truthfully (so the policy
// stays tested and the counters/UI keep working); this flag only controls
// whether saveUploadProject *enforces* it. See projectService.ts.
export const ENFORCE_UPLOAD_SLOT_LIMIT = true;

// UI mirror of the flag above: when the slot cap isn't enforced, don't show the
// slot counters / "X of Y used" framing anywhere in the UI.
// Single source of truth so the interface can't drift from the actual policy.
export const SHOW_UPLOAD_SLOT_LIMIT_UI = ENFORCE_UPLOAD_SLOT_LIMIT;
export const FREE_UPLOAD_MAX_SIZE_BYTES = FREE_UPLOAD_MAX_SIZE_MB * 1024 * 1024;

export type QuotaBlockReason = 'slot_limit' | 'size_limit';
export type ProjectSaveQuotaBlockReason = QuotaBlockReason | 'source_unavailable';
export type ProjectQuotaSourceType = NormalizedLibraryProjectSource['type'];
export type ProjectSaveQuotaSourceType = PracticeProjectSource['type'];

type QuotaPlatformCapabilities = Pick<
  PlatformCapabilities,
  | 'mode'
  | 'canPersistLocalDeviceMedia'
  | 'canCreateUploadBackedProjects'
  | 'uploadBackedProjectsAreQuotaLimited'
  | 'canShowPaymentUpgradeUi'
>;

export interface QuotaCheckResult {
  allowed: boolean;
  reason?: QuotaBlockReason;
  message?: string;
  uploadBackedCount?: number;
}

export interface ProjectSaveQuotaDecision {
  allowed: boolean;
  sourceType: ProjectSaveQuotaSourceType;
  consumesCloudUploadQuota: boolean;
  cloudQuotaChecked: boolean;
  canShowPaymentUpgradeUi: boolean;
  reason?: ProjectSaveQuotaBlockReason;
  message?: string;
  uploadBackedCount?: number;
}

export type ProjectSaveQuotaRequest =
  | {
      sourceType: 'upload';
      uid: string;
      fileSizeBytes: number;
      platformCapabilities?: QuotaPlatformCapabilities;
    }
  | {
      sourceType: 'local-device' | 'youtube';
      uid?: string;
      fileSizeBytes?: number;
      platformCapabilities?: QuotaPlatformCapabilities;
    };

export class QuotaExceededError extends Error {
  readonly reason: ProjectSaveQuotaBlockReason;
  constructor(message: string, reason: ProjectSaveQuotaBlockReason) {
    super(message);
    this.name = 'QuotaExceededError';
    this.reason = reason;
  }
}

export const sourceTypeConsumesCloudUploadQuota = (
  sourceType: ProjectQuotaSourceType
): boolean => sourceType === 'upload' || sourceType === 'legacy-video';

const mobileCloudSlotLimitMessage = (uploadBackedCount: number): string =>
  `Mobile v1 cannot add more cloud-upload slots. You have ` +
  `${FREE_UPLOAD_PROJECT_LIMIT} cloud-uploaded projects (${uploadBackedCount} used). ` +
  `Save this video locally on this device or delete a cloud-uploaded project to free a slot.`;

const defaultCloudSlotLimitMessage = (uploadBackedCount: number): string =>
  `Croop cloud uploads include ${FREE_UPLOAD_PROJECT_LIMIT} upload-backed projects ` +
  `(${uploadBackedCount} used). Delete a cloud-uploaded project to free a slot.`;

const cloudSlotLimitMessage = (
  uploadBackedCount: number,
  platformCapabilities: QuotaPlatformCapabilities
): string => (
  platformCapabilities.mode === 'ios-hybrid' || platformCapabilities.mode === 'android-hybrid'
    ? mobileCloudSlotLimitMessage(uploadBackedCount)
    : defaultCloudSlotLimitMessage(uploadBackedCount)
);

const cloudSizeLimitMessage = (
  fileSizeBytes: number,
  platformCapabilities: QuotaPlatformCapabilities
): string => {
  const sizeMB = (fileSizeBytes / (1024 * 1024)).toFixed(1);
  const base =
    `Croop cloud uploads support videos up to ${FREE_UPLOAD_MAX_SIZE_MB} MB. ` +
    `Your file is ${sizeMB} MB.`;

  if (platformCapabilities.mode === 'ios-hybrid' || platformCapabilities.mode === 'android-hybrid') {
    return `${base} Save locally on this device or choose a smaller file for cloud storage.`;
  }

  return `${base} Choose a smaller file to save it to cloud storage.`;
};

const countUploadBackedProjects = async (uid: string): Promise<number> => {
  const [projectsSnap, videosSnap] = await Promise.all([
    getDocs(userProjectsCollRef(uid)),
    getDocs(userVideosCollRef(uid))
  ]);

  const uploadProjectCount = projectsSnap.docs.filter((d: any) => {
    const data = d.data();
    return data.source?.type === 'upload';
  }).length;

  return uploadProjectCount + videosSnap.docs.length;
};

/**
 * Check whether a new upload-backed project save is permitted under the Free plan.
 * Counts upload-backed slots from both users/{uid}/projects (source.type === 'upload')
 * and legacy users/{uid}/videos (all records). YouTube projects are excluded from quota.
 * Returns without querying Firestore when the size limit is already exceeded.
 */
export async function checkUploadQuota(
  uid: string,
  fileSizeBytes: number,
  platformCapabilities: QuotaPlatformCapabilities = getCurrentPlatformCapabilities()
): Promise<QuotaCheckResult> {
  if (fileSizeBytes > FREE_UPLOAD_MAX_SIZE_BYTES) {
    return {
      allowed: false,
      reason: 'size_limit',
      message: cloudSizeLimitMessage(fileSizeBytes, platformCapabilities)
    };
  }

  const uploadBackedCount = await countUploadBackedProjects(uid);

  if (uploadBackedCount >= FREE_UPLOAD_PROJECT_LIMIT) {
    return {
      allowed: false,
      reason: 'slot_limit',
      message: cloudSlotLimitMessage(uploadBackedCount, platformCapabilities),
      uploadBackedCount
    };
  }

  return { allowed: true, uploadBackedCount };
}

/**
 * Action-level source-aware save decision for current and future callers.
 * Local-device and YouTube saves do not touch Firebase upload quota; upload
 * saves use the existing cloud-upload policy and platform payment visibility.
 */
export async function checkProjectSaveQuota(
  request: ProjectSaveQuotaRequest
): Promise<ProjectSaveQuotaDecision> {
  const platformCapabilities = request.platformCapabilities ?? getCurrentPlatformCapabilities();
  const canShowPaymentUpgradeUi = platformCapabilities.canShowPaymentUpgradeUi;
  const sourceType = request.sourceType;
  const consumesCloudUploadQuota = sourceTypeConsumesCloudUploadQuota(sourceType);

  if (request.sourceType === 'local-device') {
    if (!platformCapabilities.canPersistLocalDeviceMedia) {
      return {
        allowed: false,
        sourceType,
        consumesCloudUploadQuota,
        cloudQuotaChecked: false,
        canShowPaymentUpgradeUi,
        reason: 'source_unavailable',
        message: 'Device-local video persistence is not available in this build.'
      };
    }

    return {
      allowed: true,
      sourceType,
      consumesCloudUploadQuota,
      cloudQuotaChecked: false,
      canShowPaymentUpgradeUi
    };
  }

  if (request.sourceType === 'youtube') {
    return {
      allowed: true,
      sourceType,
      consumesCloudUploadQuota,
      cloudQuotaChecked: false,
      canShowPaymentUpgradeUi
    };
  }

  if (request.sourceType === 'upload') {
    if (!platformCapabilities.canCreateUploadBackedProjects) {
      return {
        allowed: false,
        sourceType,
        consumesCloudUploadQuota,
        cloudQuotaChecked: false,
        canShowPaymentUpgradeUi,
        reason: 'source_unavailable',
        message: 'Cloud uploads are not available in this build.'
      };
    }

    if (!platformCapabilities.uploadBackedProjectsAreQuotaLimited) {
      return {
        allowed: true,
        sourceType,
        consumesCloudUploadQuota,
        cloudQuotaChecked: false,
        canShowPaymentUpgradeUi
      };
    }

    const quotaResult = await checkUploadQuota(
      request.uid,
      request.fileSizeBytes,
      platformCapabilities
    );

    return {
      ...quotaResult,
      sourceType,
      consumesCloudUploadQuota,
      cloudQuotaChecked: true,
      canShowPaymentUpgradeUi
    };
  }

  return {
    allowed: false,
    sourceType,
    consumesCloudUploadQuota,
    cloudQuotaChecked: false,
    canShowPaymentUpgradeUi,
    reason: 'source_unavailable',
    message: 'Unsupported project source.'
  };
}
